import { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminAffiliates() {
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);

  const fetchApplications = () => apiClient.get("/admin/affiliate-applications").then((res) => setApplications(res.data));
  useEffect(() => { fetchApplications(); }, []);

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete affiliate application from "${a.full_name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/affiliate-applications/${a.id}`);
      toast.success("Application deleted");
      if (selected?.id === a.id) setSelected(null);
      fetchApplications();
    } catch {
      toast.error("Could not delete application");
    }
  };

  return (
    <div data-testid="admin-affiliates-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900">Affiliate Applications</h1>
        <span className="text-sm text-slate-500" data-testid="admin-affiliates-count">{applications.length} total</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-slate-600">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Platform</th>
              <th className="px-4 py-3">Audience</th>
              <th className="px-4 py-3">Submitted</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {applications.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`admin-affiliate-row-${a.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{a.full_name}</td>
                <td className="px-4 py-3">{a.email}</td>
                <td className="px-4 py-3">{a.primary_platform}</td>
                <td className="px-4 py-3">{a.audience_size}</td>
                <td className="px-4 py-3">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setSelected(a)} className="text-brandblue-600 hover:text-brandblue-700 font-medium text-xs mr-3" data-testid={`admin-affiliate-view-${a.id}`}>
                    View
                  </button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(a)} data-testid={`admin-delete-affiliate-${a.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {applications.length === 0 && <p className="p-6 text-center text-slate-400 text-sm" data-testid="admin-no-affiliates">No affiliate applications yet.</p>}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-slate-950/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-affiliate-detail-modal">
            <div className="flex justify-between items-start">
              <h2 className="text-lg font-display font-semibold text-slate-900">{selected.full_name}</h2>
              <button onClick={() => setSelected(null)} data-testid="admin-affiliate-modal-close"><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <p className="text-sm"><span className="font-medium text-slate-900">Email:</span> {selected.email}</p>
            <p className="text-sm"><span className="font-medium text-slate-900">Phone:</span> {selected.phone}</p>
            <p className="text-sm"><span className="font-medium text-slate-900">Website / Social:</span> {selected.website}</p>
            <p className="text-sm"><span className="font-medium text-slate-900">Primary Platform:</span> {selected.primary_platform}</p>
            <p className="text-sm"><span className="font-medium text-slate-900">Audience Size:</span> {selected.audience_size}</p>
            <div>
              <p className="text-sm font-medium text-slate-900">Promotion Plan:</p>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{selected.promotion_plan}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Reason for Applying:</p>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{selected.reason}</p>
            </div>
            <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
              Submitted {new Date(selected.created_at).toLocaleString()} · Notification email {selected.email_sent ? "sent" : "not sent"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
